import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Alert,
    Chip,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    PlayArrow as ProcessIcon,
    Warning as WarningIcon,
    AccountBalanceWallet as WalletIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';
import API from '../../../../../API';

/**
 * Format currency for display
 */
function formatCurrency(amount) {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(amount);
}

/**
 * Process Automated Dialog
 *
 * Confirmation dialog for converting approved manual withdrawals to automated processing.
 * Used when admin wants to retry an approved manual withdrawal via automated payment pipeline.
 */
function ProcessAutomatedDialog({ open, onClose, withdrawal }) {
    const queryClient = useQueryClient();

    // Process automated mutation
    const processAutomatedMutation = useMutation(
        () => API.withdrawals.processAutomated(withdrawal.id),
        {
            onSuccess: (data) => {
                toast.success('Withdrawal converted to automated processing');
                queryClient.invalidateQueries(['withdrawals']);
                queryClient.invalidateQueries(['adminWithdrawals']);
                onClose();
            },
            onError: (error) => {
                toast.error(`Failed: ${error.response?.data?.error || error.message}`);
            }
        }
    );

    const handleConfirm = () => {
        processAutomatedMutation.mutate();
    };

    if (!withdrawal) return null;

    // Determine recipient name and payment service
    const recipientName = withdrawal.creator?.creator ||
                          withdrawal.company?.name ||
                          withdrawal.user?.email ||
                          'Unknown Recipient';

    const paymentService = withdrawal.payment_service ||
                          withdrawal.creator?.user?.payout_preferred ||
                          'N/A';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={processAutomatedMutation.isLoading}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ProcessIcon sx={{ mr: 1 }} />
                    Process Automated Withdrawal
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Warning Alert */}
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
                    This will immediately process the withdrawal via the automated payment pipeline.
                    The withdrawal mode will change from <strong>manual</strong> to <strong>automated</strong>.
                </Alert>

                {/* Withdrawal Details */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Withdrawal Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Reference */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                Reference:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                #{withdrawal.reference || withdrawal.id || 'N/A'}
                            </Typography>
                        </Box>

                        {/* Amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                Amount:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" color="primary">
                                {formatCurrency(withdrawal.amount_no_fee || withdrawal.amount)}
                            </Typography>
                        </Box>

                        {/* Recipient */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                <PersonIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                Recipient:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {recipientName}
                            </Typography>
                        </Box>

                        {/* Payment Method */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                <WalletIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                                Method:
                            </Typography>
                            <Chip
                                label={paymentService}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        </Box>

                        {/* Current Status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                Current Status:
                            </Typography>
                            <Chip
                                label={withdrawal.status || 'unknown'}
                                size="small"
                                color={withdrawal.status === 'approved' ? 'success' : withdrawal.status === 'pending' ? 'warning' : 'default'}
                            />
                        </Box>

                        {/* Current Mode */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                                Current Mode:
                            </Typography>
                            <Chip
                                label={withdrawal.processing_mode || 'manual'}
                                size="small"
                                color="warning"
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Action Info */}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        After confirmation:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>Processing mode: <strong>manual → automated</strong></li>
                        <li>Status remains: <strong>{withdrawal.status || 'approved'}</strong></li>
                        <li>Payment will be initiated immediately</li>
                        <li>Metadata will be logged for audit trail</li>
                    </ul>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    disabled={processAutomatedMutation.isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={processAutomatedMutation.isLoading}
                    startIcon={processAutomatedMutation.isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <ProcessIcon />
                    )}
                >
                    {processAutomatedMutation.isLoading ? 'Processing...' : 'Confirm & Process'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ProcessAutomatedDialog;
